import { useCallback } from 'react';
import { useForm } from 'react-hook-form';

import { IntegrationSettings, SettingName } from '@xemplo/business-unit-query';
import { FinalisePayrunRequestModel } from '@xemplo/gp-types';
import { useFinalisePayrun } from '@xemplo/payrun-query';
import { safeCallAsync } from '@xemplo/safe-call';
import { ToastType, useToast } from '@xemplo/toast';

import { FINALISE_PAYRUN_COPY } from '../finalise-payrun.const';
import { UseFinalisePayrunFormProps } from '../finalise-payrun.types';

/** Seeds each checkbox from the business unit's integration setting; absent → unchecked. */
function toDefaultValues(settings: IntegrationSettings): FinalisePayrunRequestModel {
  return {
    finaliseExternalPayrun: settings[SettingName.FinalisePayrun] ?? false,
    sendPayslips: settings[SettingName.SendPayslips] ?? false,
  };
}

export function useFinalisePayrunForm({
  payrunId,
  settings,
  modalDialog,
  onSuccess,
}: UseFinalisePayrunFormProps) {
  const { addToast } = useToast();
  const { mutateAsync: finalisePayrun } = useFinalisePayrun({ payrunId });

  const methods = useForm<FinalisePayrunRequestModel>({
    defaultValues: toDefaultValues(settings),
  });

  const handleCancel = useCallback(() => {
    methods.reset();
    modalDialog?.closeDialog?.();
  }, [methods, modalDialog]);

  const handleSubmit = methods.handleSubmit(async (data: FinalisePayrunRequestModel) => {
    // Errors surface via the consumer's global handler — no local error toast.
    const [, error] = await safeCallAsync(finalisePayrun(data));
    if (error) return;

    methods.reset();
    modalDialog?.closeDialog?.();
    addToast({ text: FINALISE_PAYRUN_COPY.successToast, type: ToastType.Confirmation });
    onSuccess?.();
  });

  return { methods, handleCancel, handleSubmit };
}
